import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Building2, Plus, Search, Trash2, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';


export default function BankCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSumber, setFilterSumber] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newCode, setNewCode] = useState({ nama_bank_sumber: '', nama_bank: '', swift_code: '' });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bank-codes`);
      setCodes(res.data);
    } catch (error) {
      toast.error('Gagal memuat data kode bank');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const res = await api.post(`/bank-codes/seed`);
      toast.success(res.data.message);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal seed data');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCode.nama_bank_sumber || !newCode.nama_bank) {
      toast.error('Nama Bank Sumber dan Nama Bank wajib diisi');
      return;
    }
    setAddLoading(true);
    try {
      await api.post(`/bank-codes`, newCode);
      toast.success('Kode bank berhasil ditambahkan');
      setShowAdd(false);
      setNewCode({ nama_bank_sumber: '', nama_bank: '', swift_code: '' });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/bank-codes/${id}`);
      toast.success('Kode bank dihapus');
      fetchCodes();
    } catch (error) {
      toast.error('Gagal menghapus');
    }
  };

  // Get unique sumber banks for filter
  const sumberBanks = useMemo(() => {
    return [...new Set(codes.map(c => c.nama_bank_sumber))].sort();
  }, [codes]);

  // Filtered codes
  const filtered = useMemo(() => {
    return codes.filter(c => {
      const matchSearch = !search || 
        c.nama_bank?.toLowerCase().includes(search.toLowerCase()) ||
        c.swift_code?.toLowerCase().includes(search.toLowerCase());
      const matchSumber = !filterSumber || c.nama_bank_sumber === filterSumber;
      return matchSearch && matchSumber;
    });
  }, [codes, search, filterSumber]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="bank-codes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Kode Bank</h1>
            <p className="text-slate-500 mt-1">Referensi kode bank untuk transfer antar bank</p>
          </div>
          <div className="flex gap-2">
            <ExportMenu table="bank-codes" size="default" />
            {codes.length === 0 && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSeed} data-testid="seed-bank-codes">
                <Download className="h-4 w-4 mr-2" /> Muat Data Awal
              </Button>
            )}
            {['purchasing', 'admin'].includes(user?.role) && (
              <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowAdd(true)} data-testid="add-bank-code">
                <Plus className="h-4 w-4 mr-2" /> Tambah Kode Bank
              </Button>
            )}
          </div>
        </div>

        {/* Search & Filter */}
        <Card>
          <CardContent className="py-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-500">Cari Bank / Swift Code</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Ketik nama bank atau swift code..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-bank-code"
                  />
                </div>
              </div>
              <div className="w-64 space-y-1">
                <Label className="text-xs text-slate-500">Filter Bank Sumber</Label>
                <select
                  className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                  value={filterSumber}
                  onChange={(e) => setFilterSumber(e.target.value)}
                  data-testid="filter-sumber"
                >
                  <option value="">Semua Bank Sumber</option>
                  {sumberBanks.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="py-4 text-center">
              <p className="text-2xl font-bold text-red-700">{codes.length}</p>
              <p className="text-xs text-red-600">Total Kode Bank</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{sumberBanks.length}</p>
              <p className="text-xs text-blue-600">Bank Sumber</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="py-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{filtered.length}</p>
              <p className="text-xs text-emerald-600">Hasil Filter</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Daftar Kode Bank ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filtered.length > 0 ? (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-sm font-semibold">No</th>
                      <th className="px-4 py-3 text-sm font-semibold">Bank Sumber</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama Bank</th>
                      <th className="px-4 py-3 text-sm font-semibold">Swift Code</th>
                      {['purchasing', 'admin'].includes(user?.role) && (
                        <th className="px-4 py-3 text-sm font-semibold">Aksi</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((code, idx) => (
                      <tr key={code.id} className="border-b" data-testid={`bank-code-row-${code.id}`}>
                        <td className="px-4 py-2 text-sm text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-2 text-sm">{code.nama_bank_sumber}</td>
                        <td className="px-4 py-2 text-sm font-medium">{code.nama_bank}</td>
                        <td className="px-4 py-2 font-mono text-sm text-red-700 font-semibold">{code.swift_code || '-'}</td>
                        {['purchasing', 'admin'].includes(user?.role) && (
                          <td className="px-4 py-2">
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(code.id)} data-testid={`delete-bank-code-${code.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                {codes.length === 0 ? (
                  <div>
                    <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>Belum ada data kode bank</p>
                    <p className="text-sm mt-1">Klik "Muat Data Awal" untuk mengimpor data bank</p>
                  </div>
                ) : (
                  <p>Tidak ada data yang cocok dengan pencarian</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Tambah Kode Bank</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-3">
              <div className="space-y-1">
                <Label>Nama Bank Sumber <span className="text-red-500">*</span></Label>
                <Input
                  value={newCode.nama_bank_sumber}
                  onChange={(e) => setNewCode({...newCode, nama_bank_sumber: e.target.value})}
                  placeholder="PT BANK SYARIAH INDONESIA"
                  required
                  data-testid="new-bank-sumber"
                />
              </div>
              <div className="space-y-1">
                <Label>Nama Bank <span className="text-red-500">*</span></Label>
                <Input
                  value={newCode.nama_bank}
                  onChange={(e) => setNewCode({...newCode, nama_bank: e.target.value})}
                  placeholder="PT. BANK CENTRAL ASIA Tbk."
                  required
                  data-testid="new-bank-nama"
                />
              </div>
              <div className="space-y-1">
                <Label>Swift Code</Label>
                <Input
                  value={newCode.swift_code}
                  onChange={(e) => setNewCode({...newCode, swift_code: e.target.value})}
                  placeholder="CENAIDJA"
                  className="font-mono uppercase"
                  data-testid="new-swift-code"
                />
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={addLoading} data-testid="submit-bank-code">
                {addLoading ? 'Menyimpan...' : 'Tambah Kode Bank'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}



